import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ExportInterfaceCurrency, ExportInterfaceFieldAssignmentSourceInfoDTO, ExportInterfaceMasterDataDTO } from 'src/app/base/models/models.swagger.generated';
import { DropDownConstantsFactory } from 'src/app/base/shared/syncfusion/dropdown-constants-factory';
import { ConfigurationsService } from '../../../configurations/configurations.service';
import { ExportConfigurationMasterDataServiceService } from '../../../export-configuration-master-data.service';
import { FieldAssignmentTemplatesOverviewService } from '../../field-assignment-templates-overview/field-assignment-templates-overview.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NumericTextBoxModule } from '@syncfusion/ej2-angular-inputs';
import { EnumDropDownListComponent } from '../../../../../base/shared/enum-drop-down-list/enum-drop-down-list.component';
import { YesNoGroupButtonComponent } from '../../../../../base/shared/yes-no-group-button/yes-no-group-button.component';
import { NgClass } from '@angular/common';
import { AppErrorMessagesComponent } from '../../../../../base/error-messages/app-error-messages.component';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';

@Component({
    selector: 'app-field-assignment-entry-form',
    templateUrl: './field-assignment-entry-form.component.html',
    styleUrls: ['./field-assignment-entry-form.component.scss'],
    imports: [ReactiveFormsModule, DropDownListModule, AppErrorMessagesComponent, YesNoGroupButtonComponent, EnumDropDownListComponent, NumericTextBoxModule, NgClass, TranslateModule]
})
export class FieldAssignmentEntryFormComponent implements OnInit, OnDestroy {

  @Input() public fieldAssignmentEditForm: UntypedFormGroup;

  public get isEdit(): boolean {
    return !!this.fieldAssignmentEditForm.get('id').value;
  }

  public sourceFields: String[] = [];
  public masterData: ExportInterfaceMasterDataDTO;
  public selectedSource: ExportInterfaceFieldAssignmentSourceInfoDTO;

  public enumDropdownSettings = DropDownConstantsFactory.createEnumDropdownSettings();
  public Currency = ExportInterfaceCurrency;

  private unsubscribe$ = new Subject();

  constructor(
    private masterDataServiceService: ExportConfigurationMasterDataServiceService,
    private configurationService: ConfigurationsService,
    private fieldAssignmentTemplateService: FieldAssignmentTemplatesOverviewService,
    private translateService: TranslateService
  ) {
  }

  ngOnInit(): void {
    var configurationType = this.fieldAssignmentTemplateService.configurationType ?? this.configurationService.configurationType;
    this.sourceFields = this.masterDataServiceService.getSourceFieldsForConfigurationType(configurationType).map(v => v.sourceName);
    this.masterDataServiceService.getMasterDataForFileName()
      .pipe(
        tap(data => {
          this.masterData = data;

        }),
        switchMap(() => this.fieldAssignmentEditForm.get('sourceName').valueChanges
          .pipe(
            startWith(this.fieldAssignmentEditForm.get('sourceName').value)
          )
        ),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(sourceNameValue => {
        if (sourceNameValue) {
          this.selectedSource = this.masterDataServiceService.getSourceFieldsForConfigurationType(this.configurationService.configurationType)?.find(source => source.sourceName === sourceNameValue);
        } else {
          this.selectedSource = null;
        }
        if (!this.isFixValueVisible) {
          this.fieldAssignmentEditForm.get('fixValue').setValue(null);
        }
        if (!this.selectedSource?.canHaveCap) {
          this.fieldAssignmentEditForm.get('cap').setValue(null);
        }
        if (!this.selectedSource?.canHaveExportZeroValueAsEmpty) {
          this.fieldAssignmentEditForm.get('exportZeroValueAsEmpty').setValue(false);
        }
        if (this.selectedSource?.customField1Label != null) {
          if (this.selectedSource.customField1Optional !== true) {
            this.fieldAssignmentEditForm.get('customField1Value').setValidators([Validators.required]);
          }
          else {
            this.fieldAssignmentEditForm.get('customField1Value').setValidators([]);
          }
        } else {
          this.fieldAssignmentEditForm.get('customField1Value').setValidators([]);
          this.fieldAssignmentEditForm.get('customField1Value').setValue(null);
        }
        this.fieldAssignmentEditForm.get('customField1Value').updateValueAndValidity();

        if (this.selectedSource?.customField2Label != null) {
          if (this.selectedSource.customField2Optional !== true) {
            this.fieldAssignmentEditForm.get('customField2Value').setValidators([Validators.required]);
          }
          else {
            this.fieldAssignmentEditForm.get('customField2Value').setValidators([]);
          }
        } else {
          this.fieldAssignmentEditForm.get('customField2Value').setValidators([]);
          this.fieldAssignmentEditForm.get('customField2Value').setValue(null);
        }
        this.fieldAssignmentEditForm.get('customField2Value').updateValueAndValidity();

        if (this.selectedSource?.customField3Label != null) {
          if (this.selectedSource.customField3Optional !== true) {
            this.fieldAssignmentEditForm.get('customField3Value').setValidators([Validators.required]);
          }
          else {
            this.fieldAssignmentEditForm.get('customField3Value').setValidators([]);
          }
        } else {
          this.fieldAssignmentEditForm.get('customField3Value').setValidators([]);
          this.fieldAssignmentEditForm.get('customField3Value').setValue(null);
        }
        this.fieldAssignmentEditForm.get('customField3Value').updateValueAndValidity();

        if (this.selectedSource?.customField4Label != null) {
          if (this.selectedSource.customField4Optional !== true) {
            this.fieldAssignmentEditForm.get('customField4Value').setValidators([Validators.required]);
          }
          else {
            this.fieldAssignmentEditForm.get('customField4Value').setValidators([]);
          }
        } else {
          this.fieldAssignmentEditForm.get('customField4Value').setValidators([]);
          this.fieldAssignmentEditForm.get('customField4Value').setValue(null);
        }
        this.fieldAssignmentEditForm.get('customField4Value').updateValueAndValidity();

        if (this.selectedSource?.customField5Label != null) {
          if (this.selectedSource.customField5Optional !== true) {
            this.fieldAssignmentEditForm.get('customField5Value').setValidators([Validators.required]);
          }
          else {
            this.fieldAssignmentEditForm.get('customField5Value').setValidators([]);
          }
        } else {
          this.fieldAssignmentEditForm.get('customField5Value').setValidators([]);
          this.fieldAssignmentEditForm.get('customField5Value').setValue(null);
        }
        this.fieldAssignmentEditForm.get('customField5Value').updateValueAndValidity();
      });
  }

  public get isFixValueVisible(): boolean {
    const source = this.fieldAssignmentEditForm.get('sourceName').value;
    if (!source || source == '') {
      return true;
    }
    return this.selectedSource?.canHaveFixValue
  }

  public get isExportZeroValueAsEmptyVisible(): boolean {
    return this.selectedSource?.canHaveExportZeroValueAsEmpty;
  }

  public get isCustomField1Visible(): boolean {
    return this.selectedSource?.customField1Label != null;
  }

  public get isCustomField2Visible(): boolean {
    return this.selectedSource?.customField2Label != null;
  }

  public get isCustomField3Visible(): boolean {
    return this.selectedSource?.customField3Label != null;
  }

  public get isCustomField4Visible(): boolean {
    return this.selectedSource?.customField4Label != null;
  }

  public get isCustomField5Visible(): boolean {
    return this.selectedSource?.customField5Label != null;
  }

  public getCustomFieldLabel(labelName:string): string {
    var translateLabelPath = 'i18n.domain.export-configuration.configurations.forms.file.field-configuration.form-fields.' + labelName;
    var translatedLabel = this.translateService.instant(translateLabelPath);
    if (translatedLabel === translateLabelPath) {
      return labelName;
    }
    return translatedLabel ? translatedLabel : labelName;
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

}
